import { createSlice } from '@reduxjs/toolkit';
import { orderBy } from 'lodash';
import { DEFAULT_SORT_BY, EMPTY_ARRAY } from '../../../constants/productConfiguratorConstants';
import {
  addItemsToMapList,
  removeItemsFromMapList,
  updateItemInMapList,
  updateItemsInMapList,
} from '../../../helpers/reduxHelper';
import {
  selectConfiguratorSortBy,
  selectSelectedLabelId,
  selectSelectedOptionId,
} from './configurator.slicer';
import { isV2 } from '../../../components/Configurator/utils';
import { getAppState } from '../../store';

function defaultOption(state, labelId, loading = false, loaded = true) {
  if (state.data[labelId]) return;
  state.data[labelId] = {
    loading,
    loaded,
    options: [],
  };
}

const getInitialState = () => ({
  data: {},
});

const configuratorLabelDetails = createSlice({
  name: 'configuratorLabelDetails',
  initialState: getInitialState(),
  reducers: {
    resetState: () => {
      return getInitialState();
    },
    fetchLabelOptionsStart: (state, { payload }) => {
      const { labelId } = payload;
      defaultOption(state, labelId, true, false);
      state.data[labelId].loading = true;
      state.data[labelId].loaded = false;
      state.data[labelId].error = false;
      state.data[labelId].options = [];
    },
    fetchLabelOptionsSuccess: (state, { payload }) => {
      const { labelId, options } = payload;
      defaultOption(state, labelId, false, false);
      state.data[labelId].loading = false;
      state.data[labelId].loaded = true;
      state.data[labelId].error = false;
      state.data[labelId].options = options;
    },
    fetchLabelOptionsFailed: (state, { payload }) => {
      const { labelId } = payload;
      state.data[labelId].loading = false;
      state.data[labelId].loaded = true;
      state.data[labelId].error = true;
      state.data[labelId].options = [];
    },
    addOptionsToLabel: (state, action) => {
      defaultOption(state, action.payload.labelId);
      addItemsToMapList('options', 'labelId', 'options', 'data')(state, action);
    },
    updateLabelOption: updateItemInMapList('options', 'labelId', 'option', 'data'),
    removeOptionsFromLabel: removeItemsFromMapList('options', 'labelId', 'data'),
    updateLabelOptions: updateItemsInMapList('options', 'labelId', 'options', 'data'),
    setLabelNextPage: (state, { payload }) => {
      const { sceneId, labelId, nextPage } = payload;
      if (state.data?.[sceneId]?.[labelId]) {
        state.data[sceneId][labelId].labelNextPage = nextPage;
      } else {
        state.data[sceneId] = {
          ...state.data[sceneId],
          [labelId]: { labelNextPage: nextPage },
        };
      }
    },
    setLabelOptionsLoading: (state, { payload }) => {
      const { labelId, value } = payload;
      if (state.data[labelId]) {
        state.data[labelId].loading = value;
      } else {
        state.data[labelId] = { loading: value };
      }
    },
    addOptionsToLabelObject: (state, { payload }) => {
      const { sceneId, labelId, options, nextPage, hasMorePages } = payload;
      if (state.data[labelId]) {
        if (state.data[labelId]?.options) {
          const filteredOptions = options.filter(
            (_item) => !state.data[labelId].options.find((_option) => _option.id === _item.id)
          );
          state.data[labelId].options = [...state.data[labelId].options, ...filteredOptions];
        } else {
          state.data[labelId].options = options;
        }
      } else {
        state.data[labelId] = { options };
      }
      if (state.data?.[sceneId]?.[labelId]) {
        state.data[sceneId][labelId].labelNextPage = hasMorePages ? nextPage : null;
      } else {
        state.data[sceneId] = {
          ...state.data[sceneId],
          [labelId]: { labelNextPage: hasMorePages ? nextPage : null },
        };
      }
    },
  },
});

export const configuratorLabelDetailsActions = configuratorLabelDetails.actions;
export const configuratorLabelDetailsReducer = configuratorLabelDetails.reducer;

export const selectLabelOptions =
  (labelId, { is_enable } = {}) =>
  (state) => {
    let options = state.configuratorLabelDetails.data[labelId]?.options || EMPTY_ARRAY;
    if (typeof is_enable === 'boolean') {
      options = options.filter((o) => (o?.data?.is_enable || o?.is_enable) === is_enable);
    }
    return options;
  };

export const selectLabelOptionsLoaded = (labelId) => (state) => {
  return state.configuratorLabelDetails.data[labelId]?.loaded;
};

export const selectLabelOptionsLoading = (labelId) => (state) => {
  return state.configuratorLabelDetails.data[labelId]?.loading;
};

export const selectLabelOptionsError = (labelId) => (state) => {
  return state.configuratorLabelDetails.data[labelId]?.error || false;
};

export const selectSortedLabelOptions =
  (labelId, config = {}) =>
  (state) => {
    const options = selectLabelOptions(labelId, config)(state);
    const sortBy = selectConfiguratorSortBy(getAppState());
    return sortBy === DEFAULT_SORT_BY
      ? orderBy(options, isV2() ? 'data.seq_no' : 'seq_no', 'asc')
      : options;
  };

export const selectLabelOption = (optionId, labelId) => (state) => {
  const options = selectLabelOptions(labelId)(state);
  return options.find((o) => o.id === parseInt(optionId));
};

export const selectSelectedLabelOption = (optionId) => (state) => {
  const labelId = selectSelectedLabelId(state);
  return selectLabelOption(optionId, labelId)(state);
};

export const selectSelectedOption = (state) => {
  const labelId = selectSelectedLabelId(state);
  const optionId = selectSelectedOptionId(state);
  if (!labelId || !optionId) return null;
  return selectLabelOption(optionId, labelId)(state);
};

export const getNextLabelPage = (sceneId, labelId) => (state) => {
  return state.configuratorLabelDetails.data?.[sceneId]?.[labelId]?.labelNextPage;
};
